import React, { useMemo } from 'react';

// material-ui
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography,
  Tooltip,
  Chip
} from '@mui/material';

// hooks & store
import { useExtractionDetail } from '../../../hooks/useExtraction';
import useExtractStore from '../../../store/useExtract';
import EllipsisTooltip from '../../../components/tooltipText';

/* ================= Helper functions ================= */

const parseMetadata = (val) => {
  if (!val) return null;
  if (typeof val === 'object') return val;
  try {
    return JSON.parse(val);
  } catch (e) {
    return null;
  }
};

const renderConfidenceChip = (score) => {
  if (!score) return '-';
  
  const scoreUpper = String(score).toUpperCase();
  let style = {};
  
  if (scoreUpper === 'HIGHEST') {
    style = { bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7', fontWeight: 'bold' };
  } else if (scoreUpper === 'HIGH') {
    style = { bgcolor: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7', fontWeight: 'normal' };
  } else if (scoreUpper === 'MEDIUM' || scoreUpper === 'AVERAGE') {
    style = { bgcolor: '#fff3e0', color: '#e65100', border: '1px solid #ffe0b2', fontWeight: 'normal' };
  } else if (scoreUpper === 'LOW') {
    style = { bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2', fontWeight: 'normal' };
  } else if (scoreUpper === 'LOWEST') {
    style = { bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2', fontWeight: 'bold' };
  } else {
    style = { bgcolor: '#f5f5f5', color: '#616161', border: '1px solid #e0e0e0', fontWeight: 'normal' };
  }
  
  return (
    <Chip
      label={scoreUpper}
      size="small"
      sx={{ ...style, borderRadius: '4px', textTransform: 'uppercase', fontSize: '0.7rem', height: 20 }}
    />
  );
};

const getStructuredDetails = (value) => {
  if (value === null || value === undefined) return null;
  
  let parsed = null;
  if (Array.isArray(value)) {
    parsed = value;
  } else if (typeof value === 'object') {
    parsed = [value];
  } else if (typeof value === 'string' && (value.trim().startsWith('[') || value.trim().startsWith('{'))) {
    try {
      parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) {
        parsed = [parsed];
      }
    } catch (e) {
      return null;
    }
  }

  // Ensure it's an array of objects
  if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0] !== null) {
    return parsed;
  }
  return null;
};

const renderCellValue = (value) => {
  if (value === null || value === undefined) return '-';

  // array
  if (Array.isArray(value)) {
    return value.length ? value.map(v => typeof v === 'object' ? renderCellValue(v) : String(v)).join(', ') : '-';
  }

  // object
  if (typeof value === 'object') {
    const pairs = Object.entries(value).map(([k, v]) => {
      const valStr = typeof v === 'object' ? renderCellValue(v) : String(v);
      return `${k.replace(/_/g, ' ')}: ${valStr}`;
    });
    return pairs.join(', ');
  }

  // primitive
  return String(value);
};

const formatHeader = (col) => {
  if (col.type === 'confidence') {
    return `${col.baseKey.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase()} (CONFIDENCE)`;
  }
  return col.key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toUpperCase();
};

/* ================= Main Component ================= */

export default function ViewDataPage({ table }) {
  const {
    data: dataExtractionDetail,
    isFetching,
    isError
  } = useExtractionDetail({ table });

  // const { setHubName, setOpenProject } = useExtractStore();

  // safe rows
  const rows = dataExtractionDetail?.data ?? [];

  // dynamic columns from first row
  const columns = useMemo(() => {
    if (!rows.length) return [];
    
    const baseKeys = Object.keys(rows[0]).filter(key => key !== 'extraction_metadata');
    
    const hasMetadata = rows.some(row => row.extraction_metadata);
    if (!hasMetadata) {
      return baseKeys.map(key => ({ key, type: 'data', baseKey: key }));
    }

    const result = [];
    baseKeys.forEach(key => {
      result.push({ key, type: 'data', baseKey: key });
      
      const hasConfidence = rows.some(row => {
        const parsed = parseMetadata(row.extraction_metadata);
        return parsed?.extracted_fields?.[key]?.confidence_score;
      });
      
      if (hasConfidence) {
        result.push({ key: `${key}_confidence`, type: 'confidence', baseKey: key });
      }
    });

    return result;
  }, [rows]);

  /* ================= UI states ================= */

  if (isFetching) {
    return (
      <Box mt={5} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box mt={5}>
        <Typography align='center' color="error">Failed to load extraction data</Typography>
      </Box>
    );
  }

  if (!rows.length) {
    return (
      <Box mt={5}>
        <Typography>No data available</Typography>
      </Box>
    );
  }

  /* ================= Render ================= */

  return (
    <Box mt={2} px={2}>
      <TableContainer sx={{py: 2}}>
        <Table size="small">
          {/* ===== Table Head ===== */}
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  <strong>{formatHeader(col)}</strong>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* ===== Table Body ===== */}
          <TableBody>
            {rows.map((row, rowIndex) => (
              <TableRow key={row.id ?? rowIndex}>
                {columns.map((col) => {
                  if (col.type === 'confidence') {
                    const meta = parseMetadata(row.extraction_metadata);
                    const score = meta?.extracted_fields?.[col.baseKey]?.confidence_score;
                    return (
                      <TableCell
                        key={col.key}
                        sx={{
                          maxWidth: 240,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {renderConfidenceChip(score)}
                      </TableCell>
                    );
                  }

                  const cellVal = row[col.key];
                  const structuredDetails = getStructuredDetails(cellVal);

                  if (structuredDetails) {
                    const detailKeys = Object.keys(structuredDetails[0]);
                    return (
                      <TableCell
                        key={col.key}
                        sx={{
                          maxWidth: 240,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        <Tooltip
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Table size="small" sx={{ 
                                minWidth: 320,
                                '& td, & th': { 
                                  borderColor: '#e2e8f0', 
                                  color: '#1e293b',
                                  fontSize: '0.75rem',
                                  py: 0.5,
                                  px: 1
                                } 
                              }}>
                                <TableHead>
                                  <TableRow sx={{ '& th': { borderBottom: '2px solid #cbd5e1' } }}>
                                    {detailKeys.map(k => (
                                      <TableCell key={k}>
                                        <strong>{k.replace(/_/g, ' ').toUpperCase()}</strong>
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {structuredDetails.map((item, idx) => (
                                    <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                      {detailKeys.map(k => (
                                        <TableCell key={k}>
                                          {renderCellValue(item[k])}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          }
                          interactive
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                backgroundColor: '#ffffff',
                                color: '#1e293b',
                                boxShadow: '0px 8px 30px rgba(0, 0, 0, 0.15)',
                                maxWidth: 'none',
                                borderRadius: 1.5,
                                border: '1px solid #e2e8f0',
                                '& .MuiTableCell-root': {
                                  color: '#1e293b !important',
                                  borderColor: '#e2e8f0 !important'
                                }
                              }
                            }
                          }}
                        >
                          <Chip
                            label={`View Details (${structuredDetails.length})`}
                            size="small"
                            clickable
                            sx={{
                              bgcolor: 'transparent',
                              color: '#0284c7',
                              border: '1px solid #bae6fd',
                              fontWeight: 'medium',
                              fontSize: '0.75rem',
                              '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.08)' }
                            }}
                          />
                        </Tooltip>
                      </TableCell>
                    );
                  }

                  return (
                    <TableCell
                      key={col.key}
                      sx={{
                        maxWidth: 240,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      <EllipsisTooltip>{renderCellValue(cellVal)}</EllipsisTooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

